import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import { createGroupMdmCommand } from "@dashboard/devices/api";
import apiClient from "@dashboard/files/api/apiClient";
import { usePolicyCommands } from "@devices/components/Commands/hooks";
import { setPaneTab } from "@devices/store";
import { selectDevicesByGroupId } from "@devices/store/selectors/devices";
import { Group } from "@devices/types";
import { FileInfo } from "@files/types";
import { DialogProps, useTheme } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { RadioField, RadioFieldGorup } from "@shared/form/RadioField";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast } from "@shared/toasts/Toasts";

type FormValues = {
    action: "installImmediately" | "addToPolicy";
};
type Props = Omit<DialogProps, "onClose"> & {
    group: Group | undefined;
    file: FileInfo;
    onClose: () => void;
};
export const PickInstallationTypeDialog = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const theme = useTheme();
    const dispatch = useDispatch();
    const devices = useSelector((state) => selectDevicesByGroupId(state, props.group?.id));

    const { updatePolicyCommands } = usePolicyCommands();

    const installImmediately = async () => {
        if (!props.group) {
            return;
        }
        try {
            const url = await apiClient.getPresignedDownloadUrl(props.file.id);
            await createGroupMdmCommand({
                groupId: props.group.id,
                json: {
                    command: "installPackage",
                    source: "url",
                    url: url,
                    package: props.file.metadata?.file?.apk?.manifest?.package,
                    version: props.file.metadata?.file?.apk?.manifest?.versioncode,
                },
            });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };
    const addToPolicy = async () => {
        if (props.group) {
            const policy = {
                command: "installPackage",
                source: "storage",
                file: props.file.id,
                package: props.file.metadata?.file?.apk?.manifest?.package,
                version: props.file.metadata?.file?.apk?.manifest?.versioncode,
            };
            const json = JSON.stringify(props.group.policy ? [...props.group.policy, policy] : [policy]);
            await updatePolicyCommands({ groupId: props.group.id, policy: json });
        }
    };
    const onSubmit = async (values: FormValues) => {
        switch (values.action) {
            case "installImmediately":
                await installImmediately();
                dispatch(setPaneTab("COMMANDS"));
                break;
            case "addToPolicy":
                await addToPolicy();
                dispatch(setPaneTab("POLICY"));
                break;
            default:
                break;
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={{ action: "installImmediately" }}
            render={({ handleSubmit, submitting, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:installationType.title")}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={submitting} loading={submitting}>
                                {t("common:button.install")}
                            </PrimaryButton>
                        }
                        {...props}
                    >
                        <RadioFieldGorup name="action">
                            <RadioField
                                label={t("modals:installationType.input.installImmediately.caption")}
                                value="installImmediately"
                                helperText={t("modals:installationType.input.installImmediately.helperText")}
                                style={{ marginBottom: theme.spacing(5) }}
                            />

                            <RadioField
                                label={t("modals:installationType.input.addToPolicy.caption")}
                                value="addToPolicy"
                                helperText={t("modals:installationType.input.addToPolicy.helperText")}
                                style={{ marginBottom: theme.spacing(4) }}
                                disabled={!devices.length}
                                tooltip={!devices.length ? t("modals:installationType.tooltip.noDevices") : ""}
                            />
                        </RadioFieldGorup>
                    </Modal>
                </form>
            )}
        />
    );
};
